/** @format */

import { AxiosResponse } from "axios";
import { IAppointment, INotification, IValidationResult } from "lobbie";
import { useCallback, useEffect, useState } from "react";
import { DEFAULT_ERROR_MESSAGE } from "src/constants";
import { useLobbieAccountRedux } from "src/hooks/accounts/accounts";
import { useAxiosGetter, useAxiosPoster } from "src/hooks/useAxios";
import {
    handleError,
    isFailedRequest,
    isValidEmail,
    isValidPhoneNumber,
    logDev,
    notify,
} from "src/utils";

export const useStaffPatientAppointments = (patientId: number): [IAppointment[], boolean] => {
    const getter = useAxiosGetter();
    const [appointments, setAppointments] = useState<IAppointment[]>([]);
    const [isLoading, setLoading] = useState<boolean>(false);

    useEffect(() => {
        if (!patientId || patientId < 0) return;

        setLoading(true);
        getter(`/staff/patients/appointments/${patientId}`)
            .then((response) => {
                setLoading(false);
                const result = response && (response.data as IValidationResult | IAppointment[]);
                if (!result) {
                    if (response?.status && response.status > 399)
                        notify({
                            level: "error",
                            title: "Error getting appointments",
                            message: DEFAULT_ERROR_MESSAGE,
                        });
                } else if (isFailedRequest(result)) {
                    notify({
                        level: "error",
                        title: "Failed to get appointments",
                        message: (result as IValidationResult).message || DEFAULT_ERROR_MESSAGE,
                    });
                } else {
                    setAppointments(result as IAppointment[]);
                }
            })
            .catch((e) => {
                setLoading(false);
                handleError(e);
            });
    }, [getter, patientId, setLoading]);

    return [appointments, isLoading];
};

export const useStaffPatientNotifications = (patientId: number) => {
    const getter = useAxiosGetter();
    const [notifications, setNotifications] = useState<INotification[]>([]);

    useEffect(() => {
        if (patientId) {
            getter(`/staff/patients/notifications/${patientId}`)
                .then((response) => {
                    const result =
                        response && (response.data as IValidationResult | INotification[]);
                    if (!result) {
                        if (response?.status && response.status > 399) {
                            notify({
                                level: "error",
                                title: "Error getting notifications",
                                message: DEFAULT_ERROR_MESSAGE,
                            });
                        }
                    } else if (isFailedRequest(result)) {
                        notify({
                            level: "error",
                            title: "Failed to get notifications",
                            message: (result as IValidationResult).message || DEFAULT_ERROR_MESSAGE,
                        });
                    } else {
                        setNotifications(result as INotification[]);
                    }
                })
                .catch(handleError);
        }
    }, [getter, patientId]);

    return notifications;
};

export const useUserExists = (): [
    ("email" | "phone")[],
    (
        email: string,
        skipNotify?: boolean,
    ) => Promise<{ exists: boolean; accountPatientId?: number }>,
    (phone: string, skipNotify?: boolean) => Promise<void>,
    (email: string, skipNotify?: boolean) => Promise<void>,
    (phone: string, skipNotify?: boolean) => Promise<void>,
] => {
    const securePoster = useAxiosPoster();
    const { patientFieldName, lowerPatientFieldName } = useLobbieAccountRedux();
    const [exists, setExists] = useState<("email" | "phone")[]>([]);

    const getPatientExistsByEmail = useCallback(
        async (email: string, skipNotify?: boolean) => {
            if (!email || !isValidEmail(email)) {
                return { exists: false };
            }

            const suffix = email.split(".").last();
            if (!suffix || suffix.length < 2) {
                return { exists: false };
            }

            logDev("useUserExists - Checking if patient exists for email -", email);
            return securePoster("/staff/patients/search/email", { email })
                .then((response: AxiosResponse | void) => {
                    const result =
                        response &&
                        (response.data as {
                            exists: boolean;
                            accountPatientId?: number;
                        });
                    if (!result) {
                        return { exists: false };
                    }

                    if (result.exists) {
                        logDev("useUserExists - Set patient exists by email");
                        setExists([...exists, "email"]);
                        if (!skipNotify) {
                            notify({
                                level: "warning",
                                title: `${patientFieldName} exists.`,
                                message: `A ${lowerPatientFieldName} with the email - ${email} - already exists.`,
                            });
                        }
                    } else {
                        logDev("useUserExists - Set patient does NOT exist by email", exists);
                        if (exists.includes("email")) {
                            setExists(exists.filter((s) => s !== "email"));
                        }
                    }
                    return result;
                })
                .catch((error) => {
                    handleError(error);
                    return { exists: false };
                });
        },
        [securePoster, exists, lowerPatientFieldName, patientFieldName],
    );

    const getPatientExistsByPhone = useCallback(
        async (phone: string, skipNotify?: boolean) => {
            if (!isValidPhoneNumber(phone, true)) return;

            logDev("useUserExists - Checking if patient exists for PHONE -", phone);
            return securePoster("/staff/patients/search/phone", { phone })
                .then((response: AxiosResponse | void) => {
                    const result = response && (response.data as { exists: boolean });
                    if (result && result.exists) {
                        logDev("useUserExists - Set patient exists by phone");
                        setExists([...exists, "phone"]);
                        if (!skipNotify) {
                            notify({
                                level: "warning",
                                title: `${patientFieldName} exists.`,
                                message: `A ${lowerPatientFieldName} with the phone number - ${phone} - already exists.`,
                            });
                        }
                    } else {
                        logDev("useUserExists - Set patient does NOT exist by phone", exists);
                        if (exists.includes("phone")) {
                            setExists(exists.filter((s) => s !== "phone"));
                        }
                    }
                })
                .catch(handleError);
        },
        [securePoster, exists, lowerPatientFieldName, patientFieldName],
    );
    const getStaffExistsByEmail = useCallback(
        async (email: string, skipNotify?: boolean) => {
            if (!isValidEmail(email)) return;

            logDev("useUserExists - Checking if staff member exists for email -", email);
            return securePoster("/staff/person/search/email", { email })
                .then((response: AxiosResponse | void) => {
                    const result = response && (response.data as { exists: boolean });
                    if (result && result.exists) {
                        logDev("useUserExists - Set staff member exists by email");
                        setExists([...exists, "email"]);
                        if (!skipNotify) {
                            notify({
                                level: "warning",
                                title: "Staff member exists.",
                                message: `A staff member with the email - ${email} - already exists.`,
                            });
                        }
                    } else {
                        logDev("useUserExists - Set staff member does NOT exist by email", exists);
                        if (exists.includes("email")) {
                            setExists(exists.filter((s) => s !== "email"));
                        }
                    }
                })
                .catch(handleError);
        },
        [securePoster, exists],
    );

    const getStaffExistsByPhone = useCallback(
        async (phone: string, skipNotify?: boolean) => {
            if (!isValidPhoneNumber(phone, true)) return;

            logDev("useUserExists - Checking if staff member exists for PHONE -", phone);
            return securePoster("/staff/person/search/phone", { phone })
                .then((response: AxiosResponse | void) => {
                    const result = response && (response.data as { exists: boolean });
                    if (result && result.exists) {
                        logDev("useUserExists - Set staff member exists by phone");
                        setExists([...exists, "phone"]);
                        if (!skipNotify) {
                            notify({
                                level: "warning",
                                title: "Staff member exists.",
                                message: `A staff member with the phone number - ${phone} - already exists.`,
                            });
                        }
                    } else {
                        logDev("useUserExists - Set staff member does NOT exist by phone", exists);
                        if (exists.includes("phone")) {
                            setExists(exists.filter((s) => s !== "phone"));
                        }
                    }
                })
                .catch(handleError);
        },
        [securePoster, exists],
    );

    logDev("useUserExists - return exists?", exists);
    return [
        exists,
        getPatientExistsByEmail,
        getPatientExistsByPhone,
        getStaffExistsByEmail,
        getStaffExistsByPhone,
    ];
};
